import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
	Smartphone,
	QrCode,
	FileText,
	ChevronRight,
	Check,
	RefreshCw,
	Languages,
	Palette,
	ImageIcon,
	ShieldCheck,
	UserCheck,
	AlertTriangle,
	ArrowRight,
} from "lucide-react";
import Menu from "../components/restaurants/Menu";
import Footer from "../components/restaurants/Footer";
import CookiesBanner from "../components/CookiesBanner";
import { useMediaQuery } from "../hooks/useMediaQuery";
import CartaImage from "../assets/restaurant/carta.png";
import CartaImage2 from "../assets/restaurant/carta-2.png";
import CartaImage3 from "../assets/restaurant/carta-3.png";

const primaryColor = "#43bd98";

/** Asegura que el valor de i18n con returnObjects sea un array (evita crash si falta la clave en otro idioma). */
const asArray = (value) => (Array.isArray(value) ? value : []);

const formatIcons = [
	<Smartphone size={32} strokeWidth={1.8} />,
	<QrCode size={32} strokeWidth={1.8} />,
	<FileText size={32} strokeWidth={1.8} />,
];

const benefitIcons = [
	<RefreshCw size={24} strokeWidth={2} />,
	<Languages size={24} strokeWidth={2} />,
	<Palette size={24} strokeWidth={2} />,
	<ImageIcon size={24} strokeWidth={2} />,
	<ShieldCheck size={24} strokeWidth={2} />,
	<UserCheck size={24} strokeWidth={2} />,
];

const HeroSection = ({ t, isMobile }) => (
	<section
		style={{
			position: "relative",
			overflow: "hidden",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: isMobile ? "2rem 1.25rem 3rem" : "5rem 2rem 4rem",
			background: "linear-gradient(180deg, #f8fefc 0%, #ffffff 100%)",
			fontFamily: "'Poppins', sans-serif",
			marginTop: "70px",
			minHeight: isMobile ? "auto" : "80vh",
		}}
	>
		<div
			style={{
				maxWidth: "1200px",
				width: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				flexWrap: "wrap",
				gap: isMobile ? "2rem" : "3rem",
			}}
		>
			<motion.div
				style={{
					flex: "1 1 480px",
					textAlign: isMobile ? "center" : "left",
					order: isMobile ? 2 : 0,
				}}
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.7 }}
			>
				<motion.span
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ delay: 0.1 }}
					style={{
						display: "inline-block",
						background: `${primaryColor}15`,
						color: primaryColor,
						padding: "8px 18px",
						borderRadius: "24px",
						fontSize: "0.85rem",
						fontWeight: 600,
						marginBottom: "24px",
					}}
				>
					{t("cartaPage.hero.badge")}
				</motion.span>

				<h1
					style={{
						fontSize: isMobile ? "2rem" : "clamp(2.4rem, 5vw, 3.2rem)",
						fontWeight: 800,
						lineHeight: 1.15,
						color: "#111827",
						marginBottom: "1.25rem",
						marginTop: 0,
					}}
				>
					{t("cartaPage.hero.title")}{" "}
					<span style={{ color: primaryColor }}>
						{t("cartaPage.hero.titleHighlight")}
					</span>
				</h1>

				<p
					style={{
						fontSize: isMobile ? "1rem" : "1.15rem",
						lineHeight: 1.7,
						color: "#4b5563",
						maxWidth: "540px",
						margin: isMobile ? "0 auto 1.5rem" : "0 0 1.5rem",
					}}
				>
					{t("cartaPage.hero.subtitle")}
				</p>

				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.4 }}
					style={{
						display: "flex",
						gap: "16px",
						flexWrap: "wrap",
						marginBottom: "2rem",
						justifyContent: isMobile ? "center" : "flex-start",
					}}
				>
					{asArray(t("cartaPage.hero.benefits", { returnObjects: true })).map(
						(b, i) => (
							<span
								key={i}
								style={{
									display: "flex",
									alignItems: "center",
									gap: "6px",
									fontSize: "0.88rem",
									color: "#374151",
								}}
							>
								<Check size={16} color={primaryColor} strokeWidth={3} />
								{b}
							</span>
						),
					)}
				</motion.div>

				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.5, duration: 0.5 }}
					style={{
						display: "flex",
						gap: "12px",
						flexWrap: "wrap",
						justifyContent: isMobile ? "center" : "flex-start",
					}}
				>
					<a
						href="https://creator.applaby.com?onboarding=true&appType=Ecommerce&appSubtype=Restaurant"
						target="_blank"
						rel="noreferrer"
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: "8px",
							padding: "0.85rem 1.8rem",
							background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
							color: "#fff",
							borderRadius: "10px",
							fontWeight: 600,
							fontSize: "1rem",
							textDecoration: "none",
							boxShadow: "0 8px 24px rgba(67,189,152,0.25)",
							transition: "all 0.3s ease",
						}}
					>
						{t("cartaPage.hero.cta")} <ChevronRight size={18} />
					</a>
					<a
						href="https://www.latavolaitaliana.es/"
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: "8px",
							padding: "0.85rem 1.8rem",
							background: "#fff",
							color: "#1f2937",
							borderRadius: "10px",
							fontWeight: 600,
							fontSize: "1rem",
							border: "1px solid #d1d5db",
							textDecoration: "none",
							transition: "all 0.3s ease",
						}}
						target="_blank"
						rel="noreferrer"
					>
						{t("cartaPage.hero.ctaSecondary")}
					</a>
				</motion.div>
			</motion.div>

			<motion.div
				style={{
					flex: isMobile ? "1 1 100%" : "1 1 440px",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					order: isMobile ? 0 : 0,
					marginTop: isMobile ? "0.5rem" : 0,
					position: "relative",
					height: isMobile ? "200px" : "480px",
					width: isMobile ? "100%" : "auto",
					overflow: isMobile ? "visible" : "visible",
				}}
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.6, delay: 0.2 }}
			>
				{[
					{
						rotate: -8,
						offsetX: "2%",
						top: "8%",
						z: 1,
						delay: 0,
					},
					{
						rotate: 2,
						offsetX: "18%",
						top: "2%",
						z: 2,
						delay: 0.12,
					},
					{
						rotate: 9,
						offsetX: "34%",
						top: "10%",
						z: 3,
						delay: 0.24,
					},
				].map(({ rotate, offsetX, top, z, delay }, i) => (
					<motion.div
						key={i}
						style={{
							...(isMobile
								? {
										left: offsetX,
										transform: `translateX(calc(-50% + ${offsetX})) rotate(${rotate}deg)`,
									}
								: {
										position: "absolute",
										left: offsetX,
										transform: `rotate(${rotate}deg)`,
									}),
							top,
							zIndex: z,
						}}
						initial={{ opacity: 0, y: 30, rotate: 0 }}
						animate={{ opacity: 1, y: 0, rotate }}
						transition={{ duration: 0.6, delay: 0.3 + delay }}
					>
						<img
							src={i === 0 ? CartaImage3 : i === 1 ? CartaImage : CartaImage2}
							alt={`Carta digital ${i + 1}`}
							style={{
								minWidth: isMobile ? "240px" : "450px",
								width: isMobile ? "240px" : "450px",
								borderRadius: isMobile ? "8px" : "14px",
								boxShadow: `0 ${8 + z * 4}px ${20 + z * 8}px rgba(0,0,0,${0.08 + z * 0.03})`,
							}}
						/>
					</motion.div>
				))}
			</motion.div>
		</div>
	</section>
);

const ProblemSection = ({ t, isMobile }) => (
	<section
		style={{
			padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
			background: "#ffffff",
			fontFamily: "'Poppins', sans-serif",
		}}
	>
		<div style={{ maxWidth: "1000px", margin: "0 auto" }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6 }}
				viewport={{ once: true }}
				style={{ textAlign: "center", marginBottom: "3rem" }}
			>
				<span
					style={{
						display: "inline-block",
						background: "#fef3c7",
						color: "#92400e",
						padding: "6px 16px",
						borderRadius: "20px",
						fontSize: "0.85rem",
						fontWeight: 600,
						marginBottom: "16px",
					}}
				>
					{t("cartaPage.problem.badge")}
				</span>
				<h2
					style={{
						fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
						fontWeight: 800,
						color: "#111827",
						margin: 0,
					}}
				>
					{t("cartaPage.problem.title")}
				</h2>
			</motion.div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
					gap: "1.5rem",
				}}
			>
				{asArray(t("cartaPage.problem.items", { returnObjects: true })).map(
					(item, i) => (
						<motion.div
							key={i}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: i * 0.1 }}
							viewport={{ once: true }}
							style={{
								background: "#fffbeb",
								border: "1px solid #fde68a",
								borderRadius: "16px",
								padding: "1.75rem",
							}}
						>
							<div
								style={{
									width: "44px",
									height: "44px",
									borderRadius: "12px",
									background: "#fef3c7",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									marginBottom: "1rem",
								}}
							>
								<AlertTriangle size={22} color="#d97706" strokeWidth={2} />
							</div>
							<h3
								style={{
									fontSize: "1.05rem",
									fontWeight: 700,
									color: "#92400e",
									marginBottom: "0.5rem",
									marginTop: 0,
									lineHeight: 1.4,
								}}
							>
								{item.title}
							</h3>
							<p
								style={{
									fontSize: "0.9rem",
									color: "#78350f",
									lineHeight: 1.6,
									margin: 0,
									opacity: 0.85,
								}}
							>
								{item.text}
							</p>
						</motion.div>
					),
				)}
			</div>
		</div>
	</section>
);

const SolutionSection = ({ t, isMobile }) => (
	<section
		style={{
			padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
			background: "#f8fefc",
			fontFamily: "'Poppins', sans-serif",
		}}
	>
		<div style={{ maxWidth: "1100px", margin: "0 auto" }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6 }}
				viewport={{ once: true }}
				style={{ textAlign: "center", marginBottom: "1rem" }}
			>
				<span
					style={{
						display: "inline-block",
						background: `${primaryColor}15`,
						color: primaryColor,
						padding: "6px 16px",
						borderRadius: "20px",
						fontSize: "0.85rem",
						fontWeight: 600,
						marginBottom: "16px",
					}}
				>
					{t("cartaPage.solution.badge")}
				</span>
				<h2
					style={{
						fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
						fontWeight: 800,
						color: "#111827",
						margin: 0,
					}}
				>
					{t("cartaPage.solution.title")}
				</h2>
				<p
					style={{
						fontSize: "1.05rem",
						color: "#6b7280",
						marginTop: "1rem",
						maxWidth: "600px",
						marginInline: "auto",
						lineHeight: 1.6,
					}}
				>
					{t("cartaPage.solution.subtitle")}
				</p>
			</motion.div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
					gap: "1.5rem",
					marginTop: "3rem",
				}}
			>
				{asArray(t("cartaPage.solution.formats", { returnObjects: true })).map(
					(format, i) => (
						<motion.div
							key={i}
							initial={{ opacity: 0, y: 25 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: i * 0.15 }}
							viewport={{ once: true }}
							whileHover={{
								y: -6,
								boxShadow: "0 16px 40px rgba(67,189,152,0.15)",
							}}
							style={{
								background: "#ffffff",
								border: "1px solid #e1f3ec",
								borderRadius: "20px",
								padding: isMobile ? "1.75rem" : "2rem",
								transition: "all 0.3s ease",
								position: "relative",
								overflow: "hidden",
							}}
						>
							<div
								style={{
									position: "absolute",
									top: "-20px",
									right: "-20px",
									width: "100px",
									height: "100px",
									background: `${primaryColor}08`,
									borderRadius: "50%",
								}}
							/>
							<div
								style={{
									width: "60px",
									height: "60px",
									borderRadius: "16px",
									background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									marginBottom: "1.25rem",
									color: "#fff",
									boxShadow: "0 8px 20px rgba(67,189,152,0.25)",
								}}
							>
								{formatIcons[i]}
							</div>
							<h3
								style={{
									fontSize: "1.25rem",
									fontWeight: 700,
									color: "#111827",
									marginBottom: "0.75rem",
									marginTop: 0,
								}}
							>
								{format.title}
							</h3>
							<p
								style={{
									fontSize: "0.92rem",
									color: "#6b7280",
									lineHeight: 1.65,
									marginBottom: "1.25rem",
								}}
							>
								{format.description}
							</p>
							<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
								{asArray(format.highlights).map((h, j) => (
									<li
										key={j}
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
											marginBottom: "8px",
											fontSize: "0.88rem",
											color: "#374151",
										}}
									>
										<Check size={16} color={primaryColor} strokeWidth={2.5} />
										{h}
									</li>
								))}
							</ul>
						</motion.div>
					),
				)}
			</div>
		</div>
	</section>
);

const BenefitsSection = ({ t, isMobile }) => (
	<section
		style={{
			padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
			background: "#ffffff",
			fontFamily: "'Poppins', sans-serif",
		}}
	>
		<div style={{ maxWidth: "1100px", margin: "0 auto" }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6 }}
				viewport={{ once: true }}
				style={{ textAlign: "center", marginBottom: "3rem" }}
			>
				<span
					style={{
						display: "inline-block",
						background: `${primaryColor}15`,
						color: primaryColor,
						padding: "6px 16px",
						borderRadius: "20px",
						fontSize: "0.85rem",
						fontWeight: 600,
						marginBottom: "16px",
					}}
				>
					{t("cartaPage.benefits.badge")}
				</span>
				<h2
					style={{
						fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
						fontWeight: 800,
						color: "#111827",
						margin: 0,
					}}
				>
					{t("cartaPage.benefits.title")}
				</h2>
			</motion.div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: isMobile
						? "1fr"
						: "repeat(auto-fit, minmax(300px, 1fr))",
					gap: "1.25rem",
				}}
			>
				{asArray(t("cartaPage.benefits.items", { returnObjects: true })).map(
					(item, i) => (
						<motion.div
							key={i}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: i * 0.08 }}
							viewport={{ once: true }}
							style={{
								display: "flex",
								gap: "16px",
								padding: "1.5rem",
								background: "#f9fafb",
								borderRadius: "16px",
								border: "1px solid #f3f4f6",
								transition: "all 0.3s ease",
							}}
						>
							<div
								style={{
									width: "48px",
									height: "48px",
									minWidth: "48px",
									borderRadius: "12px",
									background: `${primaryColor}12`,
									color: primaryColor,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								{benefitIcons[i]}
							</div>
							<div>
								<h4
									style={{
										fontSize: "1rem",
										fontWeight: 700,
										color: "#111827",
										marginBottom: "4px",
										marginTop: 0,
									}}
								>
									{item.title}
								</h4>
								<p
									style={{
										fontSize: "0.9rem",
										color: "#6b7280",
										lineHeight: 1.6,
										margin: 0,
									}}
								>
									{item.text}
								</p>
							</div>
						</motion.div>
					),
				)}
			</div>
		</div>
	</section>
);

const HowItWorksSection = ({ t, isMobile }) => (
	<section
		style={{
			padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
			background: "#f8fefc",
			fontFamily: "'Poppins', sans-serif",
		}}
	>
		<div style={{ maxWidth: "900px", margin: "0 auto" }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6 }}
				viewport={{ once: true }}
				style={{ textAlign: "center", marginBottom: "3rem" }}
			>
				<span
					style={{
						display: "inline-block",
						background: `${primaryColor}15`,
						color: primaryColor,
						padding: "6px 16px",
						borderRadius: "20px",
						fontSize: "0.85rem",
						fontWeight: 600,
						marginBottom: "16px",
					}}
				>
					{t("cartaPage.howItWorks.badge")}
				</span>
				<h2
					style={{
						fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
						fontWeight: 800,
						color: "#111827",
						margin: 0,
					}}
				>
					{t("cartaPage.howItWorks.title")}
				</h2>
			</motion.div>

			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "0",
					position: "relative",
				}}
			>
				{!isMobile && (
					<div
						style={{
							position: "absolute",
							left: "39px",
							top: "40px",
							bottom: "40px",
							width: "2px",
							background: `linear-gradient(to bottom, ${primaryColor}40, ${primaryColor}10)`,
						}}
					/>
				)}

				{asArray(t("cartaPage.howItWorks.steps", { returnObjects: true })).map(
					(step, i) => (
						<motion.div
							key={i}
							initial={{ opacity: 0, x: -20 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.5, delay: i * 0.15 }}
							viewport={{ once: true }}
							style={{
								display: "flex",
								gap: isMobile ? "16px" : "24px",
								alignItems: "flex-start",
								padding: "1.5rem 0",
							}}
						>
							<div
								style={{
									width: "80px",
									height: "80px",
									minWidth: isMobile ? "56px" : "80px",
									minHeight: isMobile ? "56px" : "80px",
									maxWidth: isMobile ? "56px" : "80px",
									maxHeight: isMobile ? "56px" : "80px",
									borderRadius: "20px",
									background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									color: "#fff",
									fontSize: isMobile ? "1.4rem" : "1.8rem",
									fontWeight: 800,
									boxShadow: "0 8px 24px rgba(67,189,152,0.2)",
									position: "relative",
									zIndex: 1,
								}}
							>
								{step.number}
							</div>
							<div style={{ flex: 1, paddingTop: isMobile ? "4px" : "12px" }}>
								<h3
									style={{
										fontSize: isMobile ? "1.1rem" : "1.25rem",
										fontWeight: 700,
										color: "#111827",
										marginBottom: "6px",
										marginTop: 0,
									}}
								>
									{step.title}
								</h3>
								<p
									style={{
										fontSize: "0.95rem",
										color: "#6b7280",
										lineHeight: 1.65,
										margin: 0,
										maxWidth: "500px",
									}}
								>
									{step.text}
								</p>
							</div>
						</motion.div>
					),
				)}
			</div>
		</div>
	</section>
);

const FAQSection = ({ t, isMobile }) => {
	const [openIndex, setOpenIndex] = React.useState(null);

	return (
		<section
			style={{
				padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
				background: "#ffffff",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			<div style={{ maxWidth: "800px", margin: "0 auto" }}>
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					style={{ textAlign: "center", marginBottom: "3rem" }}
				>
					<h2
						style={{
							fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
							fontWeight: 800,
							color: "#111827",
							margin: 0,
						}}
					>
						{t("cartaPage.faq.title")}
					</h2>
				</motion.div>

				<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
					{asArray(t("cartaPage.faq.items", { returnObjects: true })).map(
						(item, i) => (
							<motion.div
								key={i}
								initial={{ opacity: 0, y: 10 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.4, delay: i * 0.06 }}
								viewport={{ once: true }}
								style={{
									border: "1px solid #e5e7eb",
									borderRadius: "14px",
									overflow: "hidden",
									background: openIndex === i ? "#f9fafb" : "#fff",
									transition: "all 0.3s ease",
								}}
							>
								<button
									onClick={() => setOpenIndex(openIndex === i ? null : i)}
									style={{
										width: "100%",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										padding: "1.25rem 1.5rem",
										background: "none",
										border: "none",
										cursor: "pointer",
										fontFamily: "'Poppins', sans-serif",
										fontSize: "1rem",
										fontWeight: 600,
										color: "#111827",
										textAlign: "left",
									}}
								>
									{item.question}
									<ArrowRight
										size={18}
										style={{
											transform:
												openIndex === i ? "rotate(90deg)" : "rotate(0deg)",
											transition: "transform 0.3s ease",
											flexShrink: 0,
											marginLeft: "12px",
											color: primaryColor,
										}}
									/>
								</button>
								<div
									style={{
										maxHeight: openIndex === i ? "300px" : "0",
										overflow: "hidden",
										transition: "max-height 0.35s ease",
									}}
								>
									<p
										style={{
											padding: "0 1.5rem 1.25rem",
											fontSize: "0.92rem",
											color: "#6b7280",
											lineHeight: 1.7,
											margin: 0,
										}}
									>
										{item.answer}
									</p>
								</div>
							</motion.div>
						),
					)}
				</div>
			</div>
		</section>
	);
};

const CTASection = ({ t, isMobile }) => (
	<section
		style={{
			padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
			background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
			fontFamily: "'Poppins', sans-serif",
			textAlign: "center",
		}}
	>
		<div style={{ maxWidth: "700px", margin: "0 auto" }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6 }}
				viewport={{ once: true }}
			>
				<h2
					style={{
						fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
						fontWeight: 800,
						color: "#ffffff",
						marginBottom: "1rem",
						marginTop: 0,
					}}
				>
					{t("cartaPage.cta.title")}
				</h2>
				<p
					style={{
						fontSize: "1.1rem",
						color: "rgba(255,255,255,0.9)",
						marginBottom: "2rem",
						lineHeight: 1.6,
					}}
				>
					{t("cartaPage.cta.subtitle")}
				</p>
				<motion.a
					href="https://creator.applaby.com?onboarding=true&appType=Ecommerce&appSubtype=Restaurant"
					target="_blank"
					rel="noreferrer"
					whileHover={{ scale: 1.04 }}
					whileTap={{ scale: 0.98 }}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: "8px",
						padding: "1rem 2.5rem",
						background: "#ffffff",
						color: primaryColor,
						borderRadius: "12px",
						fontWeight: 700,
						fontSize: "1.1rem",
						textDecoration: "none",
						boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
						transition: "all 0.3s ease",
					}}
				>
					{t("cartaPage.cta.button")} <ChevronRight size={20} />
				</motion.a>
				<p
					style={{
						fontSize: "0.85rem",
						color: "rgba(255,255,255,0.75)",
						marginTop: "1.25rem",
					}}
				>
					{t("cartaPage.cta.note")}
				</p>
			</motion.div>
		</div>
	</section>
);

const RestaurantCarta = () => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery("(max-width: 768px)");

	return (
		<>
			<Helmet>
				<title>{t("seo.restaurantCarta.title")}</title>
				<meta
					name="description"
					content={t("seo.restaurantCarta.description")}
				/>
				<meta name="keywords" content={t("seo.restaurantCarta.keywords")} />
				<link rel="canonical" href="https://applaby.com/restaurantes/carta" />
				<meta property="og:title" content={t("seo.restaurantCarta.ogTitle")} />
				<meta
					property="og:description"
					content={t("seo.restaurantCarta.ogDescription")}
				/>
				<meta
					property="og:url"
					content="https://applaby.com/restaurantes/carta"
				/>
				<meta property="og:type" content="website" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta
					name="twitter:title"
					content={t("seo.restaurantCarta.twitterTitle")}
				/>
				<meta
					name="twitter:description"
					content={t("seo.restaurantCarta.twitterDescription")}
				/>
			</Helmet>

			<Menu />
			<HeroSection t={t} isMobile={isMobile} />
			<ProblemSection t={t} isMobile={isMobile} />
			<SolutionSection t={t} isMobile={isMobile} />
			<BenefitsSection t={t} isMobile={isMobile} />
			<HowItWorksSection t={t} isMobile={isMobile} />
			<FAQSection t={t} isMobile={isMobile} />
			<CTASection t={t} isMobile={isMobile} />
			<Footer />
			<CookiesBanner />
		</>
	);
};

export default RestaurantCarta;
